/**
 * One-tap WhatsApp linking (ADR-W2). The person sends a prefilled code from their own
 * phone: that proves the number is theirs and that they agreed to messages. The dialog
 * waits and turns green by itself when the code arrives.
 *
 * Used for yourself (profile, dashboard nudge), for a doctor and for a clinic contact
 * (the planner shows the QR in person, or copies the message to paste in any chat).
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy, ExternalLink, Loader2, MessageCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import Button from '@/components/ui/Button';
import { errText, type LinkOut } from '@/lib/connections';

export default function WhatsAppLinkDialog({ open, onClose, who, self, getLink, isConnected }: {
  open: boolean;
  onClose: () => void;
  /** their name, for the copy text and the heading */
  who: string;
  /** linking your own phone (wording changes) */
  self?: boolean;
  getLink: () => Promise<LinkOut>;
  /** polled while open: true once their code has arrived */
  isConnected: () => Promise<boolean>;
}) {
  const [link, setLink] = useState<LinkOut | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) { setLink(null); setError(null); return; }
    getLink().then(setLink).catch((e) => setError(errText(e, 'Could not make the link')));
  }, [open]);   // eslint-disable-line react-hooks/exhaustive-deps

  const done = useQuery({
    queryKey: ['wa-link-done', who, link?.code],
    queryFn: isConnected,
    enabled: open && !!link,
    refetchInterval: (q) => (q.state.data ? false : 2500),
  });
  const connected = !!done.data;

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="wa-link-dialog">
      <div className="bg-bg-card border border-slate-800 rounded-2xl max-w-md w-full max-h-[92vh] overflow-y-auto shadow-card">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-800">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/15 text-emerald-300 flex items-center justify-center shrink-0"><MessageCircle size={18} /></div>
            <div>
              <h3 className="font-medium text-muted-strong">{self ? 'Connect your WhatsApp' : `Connect ${who} on WhatsApp`}</h3>
              <p className="text-xs text-muted">{self ? 'One tap on your phone. Nothing to type.' : 'They send one message from their own phone. That confirms the number and their agreement.'}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted hover:text-muted-strong" aria-label="Close"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4">
          {error && <p className="text-sm text-red-300">{error}</p>}
          {!link && !error && <div className="flex items-center gap-2 text-sm text-muted"><Loader2 size={14} className="animate-spin" /> Preparing the link...</div>}

          {connected && (
            <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 flex items-center gap-3" data-testid="wa-link-done">
              <div className="w-9 h-9 rounded-full bg-emerald-500/25 text-emerald-300 flex items-center justify-center"><Check size={18} /></div>
              <div className="text-sm">
                <div className="text-emerald-200 font-medium">Connected</div>
                <div className="text-muted text-xs">{self ? 'Your notifications will arrive on WhatsApp too.' : `${who} will now get messages on WhatsApp.`}</div>
              </div>
            </div>
          )}

          {link && !connected && !link.wa_url && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-200">
              The KAF WhatsApp number is not connected yet, so there is nowhere to send the code. An owner connects it in Connections; then this works.
            </div>
          )}

          {link && !connected && link.wa_url && (
            <>
              <ol className="space-y-3 text-sm">
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-slate-800 text-muted-strong text-xs flex items-center justify-center shrink-0">1</span>
                  <span className="text-muted-strong">{self ? 'On your phone, tap the button below, or scan the code with the phone camera.' : `On ${who.split(' ')[0]}'s phone: scan the code with the camera, or send them the message below.`}</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-slate-800 text-muted-strong text-xs flex items-center justify-center shrink-0">2</span>
                  <span className="text-muted-strong">WhatsApp opens with <b className="font-mono">link {link.code}</b> already typed. Press Send.</span>
                </li>
              </ol>
              <div className="flex flex-col items-center gap-3">
                {link.qr_svg && <img src={link.qr_svg} alt="QR code that opens WhatsApp with the code typed" className="w-48 h-48 rounded-xl bg-white p-2" data-testid="wa-link-qr" />}
                <a href={link.wa_url} target="_blank" rel="noopener noreferrer" className="w-full" data-testid="wa-link-open">
                  <Button className="w-full" icon={<ExternalLink size={14} />}>Open WhatsApp</Button>
                </a>
                {!self && (
                  <Button variant="secondary" className="w-full" icon={<Copy size={14} />} data-testid="wa-link-copy"
                    onClick={() => { navigator.clipboard?.writeText(link.share_text); toast.success('Message copied. Paste it in any chat with them.'); }}>
                    Copy a message to send them
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-muted justify-center" data-testid="wa-link-waiting">
                <Loader2 size={12} className="animate-spin" /> Waiting for the message. This window updates by itself.
              </div>
              <p className="text-[11px] text-muted text-center">The code works once and expires {new Date(link.expires_at).toLocaleDateString('en-AE', { day: 'numeric', month: 'long' })}. Reply STOP on WhatsApp at any time to stop messages.</p>
            </>
          )}
        </div>

        <div className="flex justify-end p-4 border-t border-slate-800">
          <Button variant={connected ? 'primary' : 'ghost'} onClick={onClose}>{connected ? 'Done' : 'Close'}</Button>
        </div>
      </div>
    </div>
  );
}
